import { type ApiSetting, decodeValue, encodeValue, type Enum } from '@Model';
import {
    EnumFilterExpressionOperation,
    EnumOperation,
    type Index,
    isOk,
    type ListRequest,
    replaceInList,
    type Result,
    useServerListApi,
    useSynqoHttp,
} from '@synqo/frontend-component-library';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function useSettings<TSettingName extends Enum, TGroupName extends Enum, TModuleName extends Enum>(
    path: string,
    settingGroup?: TGroupName,
    settingModule?: TModuleName,
) {
    type TSetting = ApiSetting<TSettingName, TGroupName, TModuleName>;

    const http = useSynqoHttp(path);
    const listApi = useServerListApi<TSetting>({ path });

    const listRequest = useMemo(() => {
        const listRequest: ListRequest = {
            page_no: 0,
            page_size: 500,
            full_text_search: '',
            sort_columns: [],
        };
        if (settingModule) {
            const settingModuleField: Index<TSetting> = 'setting_module';
            listRequest.filter_columns = {
                operation: EnumFilterExpressionOperation.And,
                filters: [
                    {
                        field: settingModuleField,
                        value: settingModule.toString(),
                        operator: EnumOperation.Equal,
                    },
                ],
            };
        }
        return listRequest;
    }, [settingModule]);

    const { data: settingsFromQuery, refetch } = useQuery({
        queryFn: async () => {
            const listInfo = await listApi.loadPage(listRequest);
            let allSettings = listInfo.items;
            if (settingGroup !== undefined) {
                allSettings = allSettings.filter((i) => i.setting_group === settingGroup);
            }
            return allSettings.map(decodeValue);
        },
        queryKey: ['settings', path, settingModule],
        refetchOnWindowFocus: false,
        initialData: [],
    });

    const locallyChangedSettingsRef = useRef<TSetting[]>([]);
    const [localSettings, setLocalSettings] = useState<TSetting[]>(settingsFromQuery);

    useEffect(() => {
        setLocalSettings(settingsFromQuery);
    }, [settingsFromQuery]);

    const { mutateAsync: updateSettingMutation } = useMutation({
        mutationFn: (item: TSetting) => http.put<Result<TSetting>, TSetting>(`/${item.id}`, item),
    });

    const { mutateAsync: updateSettingsMutation } = useMutation({
        mutationFn: (items: TSetting[]) => http.patch<Result<TSetting[]>, TSetting[]>(``, items),
    });

    const getSetting = useCallback(
        (settingName: TSettingName) => {
            return localSettings.find((v) => v.setting_name === settingName);
        },
        [localSettings],
    );

    const reset = useCallback(async () => {
        const refetchResult = await refetch();
        setLocalSettings(refetchResult.data ?? []);
        locallyChangedSettingsRef.current = [];
    }, [refetch]);

    const updateSetting = useCallback(
        async (setting: TSetting) => {
            const settingId = setting.id;
            const result = await updateSettingMutation(encodeValue(setting));

            if (!isOk(result)) {
                reset();
                return result;
            }

            if (result.data !== undefined) {
                const decoded = decodeValue(result.data);
                result.data = decoded;
                setLocalSettings((current) => current.map((s) => (s.id === settingId ? decoded : s)));
            }

            return result;
        },
        [reset, updateSettingMutation],
    );

    const updateSettings = useCallback(async () => {
        const payload = locallyChangedSettingsRef.current.map(encodeValue);

        const result = await updateSettingsMutation(payload);

        if (!isOk(result)) {
            reset();
            return result;
        }

        if (result.data !== undefined) {
            const responseData = result.data.map(decodeValue);
            result.data = responseData;
            setLocalSettings((current) => {
                const idsFromResponse = responseData.map((s) => s.id);
                const filtered = current.filter((c) => !idsFromResponse.includes(c.id));
                return [...filtered, ...responseData];
            });
        }

        locallyChangedSettingsRef.current = [];

        return result;
    }, [reset, updateSettingsMutation]);

    const updateSettingLocally = useCallback((setting: TSetting) => {
        setLocalSettings((current) => replaceInList(current, setting));
        locallyChangedSettingsRef.current = replaceInList(locallyChangedSettingsRef.current, setting);
    }, []);

    return {
        settings: localSettings,
        getSetting,
        updateSetting,
        updateSettings,
        updateSettingLocally,
        reset,
    };
}
